import {inject} from '@angular/core';

import {FrontendConfigServiceClient} from '@tapestry-energy/protos-frontend/commonjs/tap/frontend/Frontend_configServiceClientPb';
import {Message} from 'google-protobuf';
import {
  ClientReadableStream,
  Request,
  StatusCode,
  StreamInterceptor,
  UnaryInterceptor,
  UnaryResponse,
} from 'grpc-web';
import {Observable, Observer, TeardownLogic, defer, of, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';

import {getSsoSessionToken} from '../interceptors/auth_interceptor';
import {
  AuthHeaderStreamInterceptor,
  AuthHeaderUnaryInterceptor,
} from '../interceptors/grpc_auth_interceptors';
import {ClientReadableStreamEvent, GrpcWebError} from '../typings/grpc_typings';
import {ErrorsService} from './errors_service';

export interface GrpcClientOptions {
  unaryInterceptors?: Array<UnaryInterceptor<Message, Message>>;
  streamInterceptors?: Array<StreamInterceptor<Message, Message>>;
  format?: string;
  suppressCorsPreflight?: boolean;
  withCredentials?: boolean;
}

export interface ApiRequestOptions {
  hideErrors?: boolean;
  apiName?: string;
}

/** Base class for all gRPC-web promise client services. */
export abstract class BasePromiseClientService {
  protected abstract readonly apiUrl: string;
  private readonly errorsService = inject(ErrorsService);

  frontendConfigServiceClient(): FrontendConfigServiceClient {
    return this.createClient(FrontendConfigServiceClient, '/grpc');
  }

  request<T>(requestFn: () => Promise<T>, options?: ApiRequestOptions): Observable<T> {
    return defer(requestFn).pipe(
      tap({
        error: (error: GrpcWebError) => {
          if (error && !options?.hideErrors) {
            this.handleError(error, options?.apiName);
          }
        },
      }),
    );
  }

  /**
   * Executes an API request and transforms a GRPC NOT_FOUND error into a successful
   * completion that emits 'null'.
   */
  requestMaybeFound<T>(
    requestFn: () => Promise<T>,
    options?: ApiRequestOptions,
  ): Observable<T | null> {
    return defer(requestFn).pipe(
      catchError((error?: GrpcWebError) => {
        if (error?.code === StatusCode.NOT_FOUND) {
          // Convert to positive flow.
          return of(null);
        }
        if (error && !options?.hideErrors) {
          this.handleError(error, options?.apiName);
        }
        return throwError(() => error);
      }),
    );
  }

  stream<T>(
    requestFn: () => Promise<ClientReadableStream<T>>,
    options?: ApiRequestOptions,
  ): Observable<T> {
    return new Observable<T>((observer: Observer<T>): TeardownLogic => {
      let dataStream: ClientReadableStream<T> | null = null;
      let cancelled = false;

      requestFn().then((stream: ClientReadableStream<T>) => {
        if (cancelled) {
          stream.cancel();
          return;
        }
        dataStream = stream;
        stream.on(ClientReadableStreamEvent.DATA, (response: T) => {
          observer.next(response);
        });
        stream.on(ClientReadableStreamEvent.ERROR, (error: GrpcWebError) => {
          if (!options?.hideErrors) {
            this.handleError(error, options?.apiName);
          }
          observer.error(error);
        });
        stream.on(ClientReadableStreamEvent.END, () => {
          observer.complete();
        });
      });

      return () => {
        cancelled = true;
        if (dataStream) {
          dataStream.cancel();
        }
      };
    });
  }

  /**
   * Handles gRPC errors by forwarding them to the ErrorsService.
   * It resolves the API name by prioritizing an explicitly provided `apiName` override
   * (from options) and falling back to the `error.apiName` (the full gRPC path)
   * automatically attached by our gRPC interceptors.
   */
  private handleError(error: GrpcWebError, apiName?: string) {
    this.errorsService.setGrpcWebError(error, apiName || error.apiName);
  }

  /**
   * Returns the default client options configured with our custom interceptors.
   * All subclasses (like PromiseClientService) must pass these options when
   * instantiating gRPC-Web clients to enable automatic error path tracking.
   */
  protected getDefaultClientOptions(): GrpcClientOptions {
    return {
      unaryInterceptors: [new ErrorPathInterceptor()],
      streamInterceptors: [new ErrorPathStreamInterceptor()],
    };
  }

  protected createClient<ClientType>(
    ClientClass: new (
      host: string,
      credentials?: null | {[key: string]: string},
      options?: GrpcClientOptions,
    ) => ClientType,
    apiUrl: string,
    credentials?: null | {[key: string]: string},
    options?: GrpcClientOptions,
  ): ClientType {
    const baseOptions = options !== undefined ? options : this.getDefaultClientOptions();
    const tokenProvider = () => getSsoSessionToken();
    const clientOptions: GrpcClientOptions = {
      ...baseOptions,
      unaryInterceptors: [
        new AuthHeaderUnaryInterceptor(tokenProvider),
        ...(baseOptions.unaryInterceptors || []),
      ],
      streamInterceptors: [
        new AuthHeaderStreamInterceptor(tokenProvider),
        ...(baseOptions.streamInterceptors || []),
      ],
    };
    return new ClientClass(apiUrl, credentials ?? null, clientOptions);
  }
}

/**
 * A gRPC-Web Unary Interceptor that intercepts failed single-shot (unary) calls.
 * It extracts the full gRPC method path (e.g. '/package.Service/Method') from the
 * request descriptor and attaches it directly to the rejected error object as `apiName`.
 */
class ErrorPathInterceptor implements UnaryInterceptor<Message, Message> {
  intercept(
    request: Request<Message, Message>,
    invoker: (request: Request<Message, Message>) => Promise<UnaryResponse<Message, Message>>,
  ): Promise<UnaryResponse<Message, Message>> {
    const path = request.getMethodDescriptor().getName();
    return invoker(request).catch((error: unknown) => {
      if (error && typeof error === 'object') {
        (error as GrpcWebError).apiName = path;
      }
      throw error;
    });
  }
}

/**
 * A gRPC-Web Stream Interceptor that intercepts streaming calls.
 * It monkey-patches the returned stream's `.on()` event listener registration method.
 * When an 'error' event listener is registered, it wraps the callback to automatically
 * inject the full gRPC method path (e.g. '/package.Service/Method') into the propagated
 * error object before passing it to the original listener.
 */
class ErrorPathStreamInterceptor implements StreamInterceptor<Message, Message> {
  intercept(
    request: Request<Message, Message>,
    invoker: (request: Request<Message, Message>) => ClientReadableStream<Message>,
  ): ClientReadableStream<Message> {
    const path = request.getMethodDescriptor().getName();
    const stream = invoker(request);
    const originalOn = stream.on;

    // Monkey-patch the stream's event registration method.
    // We use a standard function (not an arrow function) so we can dynamically intercept
    // event registrations on the stream instance.
    stream.on = function <Payload>(eventType: string, callback: (arg: Payload) => void) {
      if (eventType === 'error') {
        // Wrap the user's error callback to inject the gRPC path
        const wrappedCallback = (err: unknown) => {
          if (err && typeof err === 'object') {
            (err as GrpcWebError).apiName = path;
          }
          // Cast 'err' to 'Payload' because we know at runtime that the callback
          // passed for the 'error' event expects the error type (Payload).
          callback(err as Payload);
        };
        // Explicitly bind the original method call to the 'stream' instance
        // to guarantee the correct execution context is preserved.
        return originalOn.call(stream, eventType, wrappedCallback);
      }
      return originalOn.call(stream, eventType, callback);
    };
    return stream;
  }
}
